import React, { createContext, PropsWithChildren, useContext } from 'react';

enum DecorationTypes {
    NONE = 'NONE',
    UNDERLINE = 'UNDERLINE',
}

interface MessagePart {
    text: string;
    decoration: DecorationTypes;
}

const DISMISS_ALERT = 'alerts/DISMISS_ALERT';

interface Alert {
    actionType: typeof DISMISS_ALERT;
    decorations: Array<{ type: DecorationTypes; length: number }>;
    message: string;
    toastId?: string | number;
}

export type ShowErrorAlert = (alert: Alert) => void;

const getDecorations = (
    messageParts: Array<MessagePart>
): {
    type: DecorationTypes;
    length: number;
}[] => {
    return messageParts.map((messagePart) => ({
        type: messagePart.decoration,
        length: messagePart.text.length,
    }));
};

const getMessage = (messageParts: Array<MessagePart>): string => {
    return messageParts.map((messagePart) => messagePart.text).join('');
};

const buildAlert = (messageParts: Array<MessagePart>, toastId?: string | number): Alert => {
    return {
        actionType: DISMISS_ALERT,
        decorations: getDecorations(messageParts),
        message: getMessage(messageParts),
        toastId,
    };
};

interface ShowFailedToSendAlert {
    (agencyEventNumber: string, infoText: string, errorMessage: string | undefined): void;
}

export const useShowErrorAlert = (): {
    showFailedToSendAlert: ShowFailedToSendAlert;
    showFailedToDeleteAlert: (agencyEventNumber: string) => void;
} => {
    const showErrorAlert = useContext(ShowErrorAlertContext);

    const showFailedToSendAlert: ShowFailedToSendAlert = (
        agencyEventNumber,
        infoText,
        errorMessage
    ) => {
        showErrorAlert?.(
            buildAlert([
                {
                    text: `Failed to send "${infoText}" to `,
                    decoration: DecorationTypes.NONE,
                },
                {
                    text: `EVENT #${agencyEventNumber}`,
                    decoration: DecorationTypes.UNDERLINE,
                },
                {
                    text: `. ${
                        errorMessage ??
                        'An unexpected error occurred, please try again. If the problem persists contact your administrator.'
                    }`,
                    decoration: DecorationTypes.NONE,
                },
            ])
        );
    };

    const showFailedToDeleteAlert = (agencyEventNumber: string): void => {
        showErrorAlert?.(
            buildAlert(
                [
                    {
                        text: 'Failed to delete an additional info from ',
                        decoration: DecorationTypes.NONE,
                    },
                    {
                        text: `EVENT #${agencyEventNumber}`,
                        decoration: DecorationTypes.UNDERLINE,
                    },
                    {
                        text: `. An unexpected error occurred, please try again. If the problem persists contact your administrator.`,
                        decoration: DecorationTypes.NONE,
                    },
                ],
                `additional-infos-delete-error-${agencyEventNumber}`
            )
        );
    };

    return { showFailedToSendAlert, showFailedToDeleteAlert };
};

const ShowErrorAlertContext = createContext<ShowErrorAlert | undefined>(undefined);

export const ShowErrorAlertProvider: React.FC<
    PropsWithChildren<{
        showErrorAlert: ShowErrorAlert;
    }>
> = ({ children, showErrorAlert }) => {
    return (
        <ShowErrorAlertContext.Provider value={showErrorAlert}>
            {children}
        </ShowErrorAlertContext.Provider>
    );
};
